
import { VCCSupportSite } from "@/global-scripts/vcc-support-site.js";
import { VCCArticle } from "@/global-scripts/vcc-support-article.js";
import { Component, Vue, Watch } from "vue-property-decorator";
import { PrintObject } from "@/models/PrintObject";
import { getModule } from "vuex-module-decorators";
import FilterModule from "@/store/modules/FilterModule";
import ContentModule from "@/store/modules/ContentModule";
import PrintBasketModule from "@/store/modules/PrintBasketModule";
import { Api } from "@/api/Api";
import { TopicModel } from "@/models/TopicModel";
import "imagesloaded";
import { TopicHelper } from "@/global-scripts/topic-helper";
import { VariantFilter } from "@/models/VariantFilter";
import { Helpers } from "@/global-scripts/helpers";
import { DocumentGenerateHelper } from "@/utils/DocumentGenerateHelper.js";

import $ from "jquery";
import MemberModule from "@/store/modules/MemberModule";

/* eslint-disable-next-line */
import ClickEvent = JQuery.ClickEvent;
import { PrintGroup } from "@/models/PrintGroup";
import {ValidityDomains} from "@/settings/ValidityDomains";
import {Config} from "@/settings/Config";

const LONG_WORD_CHAR_COUNT = 16;
const MIN_LOADER_VISIBLE = 800;

@Component
export default class PrintPreview extends Vue {
  readonly filterModule = getModule(FilterModule, this.$store);
  readonly contentModule = getModule(ContentModule, this.$store);
  readonly printBasketModule = getModule(PrintBasketModule, this.$store);
  readonly memberModule = getModule(MemberModule, this.$store);  

  vccSupportArticle: VCCArticle;
  vccSupportSite: VCCSupportSite;
  loaded = false;
  loadCount = 0; // Number of topics in active filter that has been loaded
  printObjects: PrintGroup[] = [];
  pdfGenerating = false;
  docxGenerating = false;
  pdfLink = "";
  docxLink = "";
  docxContent = [];
  documentError = false;
  docHelper: DocumentGenerateHelper;
  dataInitiated = false;
  topicHtml: string[] = [];

  /**
   * Number of topics to load
   */
  get count(): number {
    return this.printList.length;
  }

  /**
   * Number of topics in document basket that are valid for current variant filter
   */
  get topicsNotInFilter(): PrintObject[] {
    const result = [];
    this.printObjects.forEach((p) => {
      // Only show groups with parent category (E.g. do not show Polestar categories on Volvo variant filter)
      // if (p.parent) {
      p.topics.forEach((t) => {
        if (!t.inActiveFilter) {
          result.push(t);
        }
      });
      // }
    });

    return result;
  }

  /**
   * Number of topics in document basket that are NOT valid for current variant filter
   */
  get printList(): PrintObject[] {
    const result = [];
    if (this.printObjects && this.printObjects.length > 0) {
      this.printObjects.forEach((o) => {        
        // Only show groups with parent category (E.g. do not show Polestar categories on Volvo variant filter)
        if (o.parent) {
          o.topics.forEach((t) => {
            if (t.inActiveFilter) {
              result.push(t);
            }
          });
        }
      });
    }

    return result;
  }

  get pdfButtonClass(): string {
    const classes = [];

    if (this.pdfGenerating) {
      classes.push("loading");
    }
    if (this.pdfLink.length > 0) {
      classes.push("download");
    }

    return classes.join(" ");
  }

  get docxButtonClass(): string {
    const classes = [];

    if (this.docxGenerating) {
      classes.push("loading");
    }
    if (this.docxLink.length > 0) {
      classes.push("download");
    }

    return classes.join(" ");
  }

  get pdfButtonText(): string {
    if (this.pdfLink.length > 0) {
      return this.$t.PrintDownloadPdf;
    }
    return this.$t.PrintGeneratePdf;
  }

  get docxButtonText(): string {
    if (this.docxLink.length > 0) {
      return this.$t.PrintDownloadWord;
    }
    return this.$t.PrintGenerateWord;
  }

  get pdfDocumentTitle(): string {
    if (!this.pdfLink) {
      return this.documentTitle;
    }

    return this.pdfLink.substring(this.pdfLink.lastIndexOf("/") + 1, this.pdfLink.lastIndexOf(".pdf"));
  }

  get docxDocumentTitle(): string {
    if (!this.docxLink) {
      return this.documentTitle;
    }

    return this.docxLink.substring(this.docxLink.lastIndexOf("/") + 1, this.docxLink.lastIndexOf(".docx"));
  }

  get documentTitle(): string {
    let filters = [];

    if (this.filterModule.market) {
      const localizedTitle = this.$t.getLocalizedFilterString(this.filterModule.market.entry);
      filters.push(localizedTitle);
    }
    if (this.filterModule.model) {
      filters.push(this.filterModule.model.entry);
    }
    if (this.filterModule.structureWeek) {
      filters.push(this.filterModule.structureWeek.entry);
    }
    if (this.filterModule.propulsion) {
      const localizedTitle = this.$t.getLocalizedFilterString(this.filterModule.propulsion.entry);
      filters.push(localizedTitle);
    }

    return filters.join("-");
  }

  get locale(): string {
    return this.contentModule.locale;
  }

  get dataReady(): boolean {
    return this.contentModule.allDataLoaded && this.printBasketModule.printBasket != undefined;
  }

  // Load all topic html
  async loadPrintObjects() {
    const allTopics = this.contentModule.filteredTopics.slice(0);
    const printGroups = this.printBasketModule.groupedPrintObjects;
    const config = new Config();

    for (let i = 0; i < printGroups.length; i++) {
      const group = printGroups[i];

      for (let n = 0; n < group.topics.length; n++) {
        let basketItem = group.topics[n];

        if (basketItem.inActiveFilter) {          
          let topic = allTopics.find((item) => this.printBasketModule.topicSameAsBasketItem(item, basketItem));
          
          let html = "";
          if (topic) {
            html = await this.loadTopicHtml(topic);
          } else {
            console.error("Could not find print object with id " + basketItem.objectID);
          }
          
          this.topicHtml.push(html);
        }
      }
    }

    this.printObjects = printGroups;
    this.onPreviewReady();
  }

  async loadTopicHtml(topic: TopicModel): Promise<string> {
    const result = await Api.loadTopic(topic.id, this.locale, this.filterModule.platform, topic.ref);

    let html = $(result);

    const hostname = window.location.hostname;
    const isLocalDev = hostname.indexOf("local") > -1;
    
    $("img", html).each((index, element) => {
      const $el = $(element);
      let src = $el.attr("src");
      
      /*if (isLocalDev) {
        src = `${this.$config.umbracoDevServer[this.$config.portal].host}${src}`;
      }*/
      
      $el.attr("data-src-full", src);
      $el.attr("src", `${src}?width=${this.$config.defaultImageWidth}`);
    });

    return html[0].outerHTML;
  }

  titleHasLongWord(topic: PrintObject): boolean {
    // Check if title is a long word title
    const titleSplit = topic.title ? topic.title.split(" ") : [];
    let highestCharCount = 0;

    for (let i = 0; i < titleSplit.length; i++) {
      if (titleSplit[i].length > highestCharCount) {
        highestCharCount = titleSplit[i].length;
      }
    }
    return highestCharCount > LONG_WORD_CHAR_COUNT;
  }

  onPreviewReady() {
    setTimeout(() => {
      this.loaded = true;

      // Run script
      this.$nextTick(() => {
        // Apply validity
        TopicHelper.applyContentValidity(
          $(".topic-article"),
          this.filterModule.activeFilters,
          this.$config,
          this.$validityHelper
        );

        this.vccSupportArticle = new VCCArticle(this.vccSupportSite);

        const gearboxFilter = this.filterModule.activeFilters.find(
          (f) => f.domain === ValidityDomains.gearboxes
        );
        const structureWeekFilter = this.filterModule.activeFilters.find(
          (f) => f.domain === ValidityDomains.structureWeek
        );

        TopicHelper.addGearboxHighlight(gearboxFilter as VariantFilter<string[]>, this.$t);
        TopicHelper.highlightNews(structureWeekFilter as VariantFilter<string>);

        Helpers.loadingComplete();

        // Wait for images to load and then add thumbnail images
        $(".topic-article").imagesLoaded(() => {
          this.initSpinners();
          this.addVideoThumbnails();
        });
      });
    }, 250);
  }

  generatePDF(e: MouseEvent) {
    if (this.pdfLink.length > 0) {
      this.onDocumentDownload("pdf");
      return;
    }

    e.preventDefault();

    let t0 = performance.now();

    this.pdfGenerating = true;

    const documentParts = this.docHelper.getPDFDocumentParts(this.locale);

    this.$nextTick(async () => {
      try {
        this.pdfLink = await Api.generatePDF(
          this.printObjects.length,
          documentParts,
          this.documentTitle,
          this.locale
        );

        this.pdfGenerating = false;
        this.documentError = false;
      } catch (err) {
        console.error(err);

        let t1 = performance.now();

        const diff = t1 - t0;
        const delay = Math.max(MIN_LOADER_VISIBLE - diff, 0);

        // Make sure the loader is visible for a short period
        setTimeout(() => {
          this.documentError = true;
          this.pdfGenerating = false;
        }, delay);
      }
    });
  }

  generateDocx(e: MouseEvent) {
    if (this.docxLink.length > 0) {
      this.onDocumentDownload("docx");
      return;
    }

    e.preventDefault();

    let t0 = performance.now();

    this.docxGenerating = true;

    const documentParts = this.docHelper.getWordDocumentParts();
    this.$nextTick(async () => {
      try {
        this.docxLink = await Api.generateDocx(
          this.printObjects.length,
          documentParts,
          this.documentTitle,
          this.locale
        );
        this.docxGenerating = false;
        this.documentError = false;
      } catch (err) {
        console.error(err);

        let t1 = performance.now();

        const diff = t1 - t0;
        const delay = Math.max(MIN_LOADER_VISIBLE - diff, 0);

        // Make sure the loader is visible for a short period
        setTimeout(() => {
          this.documentError = true;
          this.docxGenerating = false;
        }, delay);
      }
    });
  }

  initSpinners() {
    const spinners = $(".spinner-outer");

    spinners.each((index, element) => {
      const spinner = $(element);
      spinner.remove();
    });
  }

  addVideoThumbnails() {
    $(".multimedia").each((index, element) => {
      const $element = $(element);

      // Remove empty titles
      const videoTitle = $("h4", $element);
      if (videoTitle.length && videoTitle.text().trim().length === 0) {
        videoTitle.remove();
      }

      const video = $("video", $element);
      const videoThumbnail = $('<img class="video-thumbnail"></img>');
      videoThumbnail.attr("src", video.attr("poster"));
      $element.append(videoThumbnail);
    });
  }

  onDocumentDownload(documentType: string) {
    this.$tracker.trackPrintBasketEvent(`${documentType} download`, window.location.href);
  }

  initiateData() {
    this.dataInitiated = true;
    this.loadPrintObjects();
  }

  navigateBack() {
    this.$router.go(-1);
  }

  initiate() {
    Helpers.setPageTitle("Export");
    this.docHelper = new DocumentGenerateHelper();
    this.vccSupportSite = new VCCSupportSite();
    Helpers.loadingStart();

    if (this.dataReady) {
      this.initiateData();
    }
  }

  @Watch("dataReady")
  onDataReadyChange(newVal: boolean) {
    if (newVal && !this.dataInitiated) {
      this.initiateData();
    }
  }

  created(): void {
    $("body").addClass("print-preview");

    this.initiate();
    this.$nextTick(() => {
      window.scrollTo(0, 0);
    });
  }

  mounted(): void {
    this.loadCount = 0;
  }
}
